import DragIndicator from '@mui/icons-material/DragIndicatorRounded';
import { Box, IconButton } from '@mui/material';
import { motion, useDragControls, useMotionValue } from 'framer-motion';
import { PointerEvent, useCallback, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { WherebyEmbed } from './components/Whereby';

const useStyles = makeStyles()((theme) => ({
  popupContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[4],
    zIndex: 99999,

    position: 'fixed',
    top: 80,
    left: 24,

    maxWidth: 'calc(100vw - 48px)',
    maxHeight: 'calc(100vh - 104px)',
    '& whereby-embed': {
      width: '100%',
      flex: 1,
      borderRadius: theme.shape.borderRadius,
      '& iframe': {
        borderRadius: theme.shape.borderRadius,
      },
    },
  },
  draggableArea: {
    position: 'fixed',
    top: 80,
    left: 24,
    width: 'calc(100vw - 48px)',
    height: 'calc(100vh - 104px)',
    zIndex: -1,
  },
  dragButton: {
    cursor: 'grab',
    position: 'absolute',
    top: 0,
    right: 0,
    touchAction: 'none',
  },
}));

type VideoCallPopupProps = {
  onLeave: () => void;
  room: string;
};

export const VideoCallPopup = ({ onLeave, room }: VideoCallPopupProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const { classes } = useStyles();
  const constraintsRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const width = useMotionValue(450);
  const height = useMotionValue(300);
  const controls = useDragControls();

  const handleDrag = (event: PointerEvent) => {
    controls.start(event);
  };

  const handleResizeY = useCallback(
    (_event, info) => {
      const newHeight = height.get() + info.delta.y;
      if (newHeight > 175 && newHeight < 500) {
        height.set(newHeight);
      }
    },
    [height],
  );

  const handleResizeX = useCallback(
    (_event, info) => {
      const newWidth = width.get() + info.delta.x;
      if (newWidth > 175 && newWidth < 800) {
        width.set(newWidth);
      }
    },
    [width],
  );

  const handleResizeXY = useCallback(
    (event, info) => {
      handleResizeY(event, info);
      handleResizeX(event, info);
    },
    [handleResizeX, handleResizeY],
  );

  const toggleDrag = () => setIsDragging((current) => !current);

  return (
    <>
      <motion.div className={classes.draggableArea} ref={constraintsRef} />
      <motion.div
        ref={popupRef}
        className={classes.popupContainer}
        style={{ width, height }}
        drag
        dragListener={false}
        dragControls={controls}
        dragConstraints={constraintsRef}
        onDragEnd={toggleDrag}
        onDragStart={toggleDrag}
      >
        <Box
          sx={{
            position: 'relative',
            flex: '1',
            display: 'flex',
          }}
        >
          {isDragging && (
            <Box
              sx={{
                position: 'absolute',
                height: '100%',
                width: '100%',
              }}
            />
          )}
          <WherebyEmbed room={room} onLeave={onLeave} />
          <IconButton className={classes.dragButton} onPointerDown={handleDrag} size="large">
            <DragIndicator />
          </IconButton>
          <motion.div
            style={{
              height: 10,
              width: '100%',
              cursor: 'row-resize',
              textAlign: 'center',
              position: 'absolute',
              bottom: 0,
              touchAction: 'none',
            }}
            drag="y"
            dragConstraints={{ top: 0, left: 0, right: 0, bottom: 0 }}
            dragElastic={0}
            dragMomentum={false}
            onDrag={handleResizeXY}
            onDragEnd={toggleDrag}
            onDragStart={toggleDrag}
          />
          <motion.div
            style={{
              width: 10,
              height: '100%',
              cursor: 'col-resize',
              textAlign: 'center',
              position: 'absolute',
              top: 0,
              right: 0,
              touchAction: 'none',
            }}
            drag="x"
            dragConstraints={{ top: 0, left: 0, right: 0, bottom: 0 }}
            dragElastic={0}
            dragMomentum={false}
            onDrag={handleResizeXY}
            onDragEnd={toggleDrag}
            onDragStart={toggleDrag}
          />
        </Box>
      </motion.div>
    </>
  );
};
